import { Box, Flex, Text, Tooltip } from "@mantine/core";
import { ReactNode } from "react";
import classes from "./list.module.css";

export type StatCardTone = "gray" | "green" | "dark" | "red" | "neutral";

export type StatCardItem = {
  key: string;
  label: string;
  /** Compact numeric display (e.g. "75.22") — main font, not mono */
  value: ReactNode;
  unit?: string;
  /** Full amount for tooltip when value is abbreviated */
  fullValue?: string;
  hint?: string;
  icon?: ReactNode;
  tone?: StatCardTone;
  /** 0–100 progress ratio; omit to hide bar */
  progress?: number | null;
  onClick?: () => void;
};

const TONE_VAR: Record<StatCardTone, string> = {
  gray: "var(--mantine-color-gray-7)",
  green: "var(--mantine-color-green-6)",
  dark: "var(--mantine-color-gray-9)",
  red: "var(--mantine-color-red-5)",
  neutral: "var(--mantine-color-gray-6)",
};

type Props = {
  items: StatCardItem[];
};

export function StatCards({ items }: Props) {
  const cards = items.slice(0, 4);
  if (cards.length === 0) {
    return null;
  }
  // Progress-гүй карт хоосон зураас зурахгүй; бусад нь зураастай бол өндрөө тэнцүүлэхийн тулд нуусан орон зай үлдэнэ.
  const anyProgress = cards.some((c) => c.progress != null);

  return (
    <Box
      className={classes.statGrid}
      __vars={{ "--stat-cols": String(cards.length) }}
    >
      {cards.map((item) => {
        const tone = item.tone ?? "neutral";
        const colorVar = TONE_VAR[tone] ?? TONE_VAR.neutral;
        const progress =
          item.progress != null && Number.isFinite(item.progress)
            ? Math.max(0, Math.min(100, item.progress))
            : 0;

        const valueNode = (
          <Flex align="baseline" gap={6} wrap="wrap">
            <Text component="span" className={classes.statValue}>
              {item.value}
            </Text>
            {item.unit && (
              <Text component="span" className={classes.statUnit}>
                {item.unit}
              </Text>
            )}
          </Flex>
        );

        return (
          <Box
            key={item.key}
            className={`${classes.statCard}${item.onClick ? ` ${classes.statCardClickable}` : ""}`}
            __vars={{ "--stat-dot-color": colorVar }}
            onClick={item.onClick}
            role={item.onClick ? "button" : undefined}
            tabIndex={item.onClick ? 0 : undefined}
            onKeyDown={
              item.onClick
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      item.onClick?.();
                    }
                  }
                : undefined
            }
          >
            <Flex align="center" gap={8}>
              <Box component="span" className={classes.statDot} />
              <Text className={classes.statLabel}>{item.label}</Text>
            </Flex>
            {item.fullValue ? (
              <Tooltip label={item.fullValue} withArrow>
                <Box>{valueNode}</Box>
              </Tooltip>
            ) : (
              valueNode
            )}
            {item.progress != null ? (
              <Box className={classes.statProgress}>
                <Box className={classes.statProgressBar} w={`${progress}%`} />
              </Box>
            ) : anyProgress ? (
              <Box className={`${classes.statProgress} ${classes.statProgressGhost}`} aria-hidden />
            ) : null}
            <Text className={classes.statHint}>{item.hint || "\u00a0"}</Text>
          </Box>
        );
      })}
    </Box>
  );
}
